import React, { useState } from 'react';
import { useTournaments } from '../../../shared/hooks/useTournaments';
import { useFavorites } from '../../../shared/contexts/FavoritesContext';

// Reusable loading component for consistency
const TabLoader: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-8">
    <div className="flex items-center space-x-2">
      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <span className="text-sm text-gray-600">{message}</span>
    </div>
  </div>
);

const CompetitionsSidebar: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(new Set());
  const { tournaments, loading, error } = useTournaments();
  const { isTournamentFavorite, toggleTournamentFavorite } = useFavorites();

  const filteredTournaments = tournaments.filter(tournament =>
    tournament.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group tournaments by country
  const tournamentsByCountry = filteredTournaments.reduce((acc, tournament) => {
    const country = tournament.country || 'Other';
    if (!acc[country]) {
      acc[country] = [];
    }
    acc[country].push(tournament);
    return acc;
  }, {} as Record<string, typeof filteredTournaments>);

  const toggleCountry = (country: string) => {
    const newExpanded = new Set(expandedCountries);
    if (newExpanded.has(country)) {
      newExpanded.delete(country);
    } else {
      newExpanded.add(country);
    }
    setExpandedCountries(newExpanded);
  };

  // Check if a country has any favorite tournaments
  const hasFavoriteTournaments = (country: string): boolean => {
    const countryTournaments = tournamentsByCountry[country] || [];
    return countryTournaments.some(tournament => isTournamentFavorite(tournament.id));
  };

  const getCountryCode = (country: string): string => {
    const countryMap: Record<string, string> = {
      'Germany': 'de',
      'Europe': 'eu',
      'India': 'in',
      'Spain': 'es',
      'France': 'fr',
      'United States': 'us',
      'England': 'gb',
      'Italy': 'it',
      'Other': 'un'
    };
    return countryMap[country] || 'un';
  };

  if (loading) {
    return <TabLoader message="Loading tournaments..." />;
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-gray-500">Failed to load tournaments</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="text-base font-semibold text-gray-900 mb-2">Tournaments</div>
      
      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search tournaments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Tournaments List Grouped by Country */}
      <div className="space-y-2">
        {Object.entries(tournamentsByCountry).map(([country, countryTournaments]) => (
          <div key={country} className="space-y-1">
            {/* Country Header */}
            <button
              onClick={() => toggleCountry(country)}
              className="flex items-center justify-between w-full px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="flex items-center space-x-2">
                <img 
                  src={`https://flagcdn.com/w20/${getCountryCode(country)}.png`}
                  alt={`${country} flag`}
                  className="w-4 h-3 rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/16x12/cccccc/666666?text=🏳️';
                  }}
                />
                <span>{country}</span>
                {hasFavoriteTournaments(country) && (
                  <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                )}
              </div>
              <svg
                className={`w-4 h-4 transition-transform ${
                  expandedCountries.has(country) ? 'rotate-90' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Tournaments under Country */}
            {expandedCountries.has(country) && (
              <div className="ml-4 space-y-1">
                {countryTournaments.map((tournament) => (
                  <div
                    key={tournament.id}
                    className="flex items-center justify-between px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <img 
                        src={tournament.logo || 'https://via.placeholder.com/16x16/cccccc/666666?text=⚽'} 
                        alt={`${tournament.name} logo`}
                        className="w-4 h-4 rounded object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/16x16/cccccc/666666?text=⚽';
                        }}
                      />
                      <span className="truncate">{tournament.name}</span>
                    </div>
                    <button
                      onClick={() => toggleTournamentFavorite(tournament.id)}
                      className={`p-1 rounded-full transition-colors ${
                        isTournamentFavorite(tournament.id)
                          ? 'text-yellow-500 hover:text-yellow-600'
                          : 'text-gray-400 hover:text-yellow-500'
                      }`}
                    >
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredTournaments.length === 0 && searchQuery && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">No tournaments found</p>
        </div>
      )}
    </div>
  );
};

export default CompetitionsSidebar;